// Higiene de credenciais — R9 opcao 1 do PLANO-ZK-SELF-SERVICE-PCP (§7.2).
//
// PORQUE existe: com validade indefinida (F2, `expires_at IS NULL`) NADA mais
// expira sozinho, e o teto de credenciais ativas vira ABSOLUTO — so a revogacao
// manual do master libera vaga. Um originador que siga a recomendacao deste
// plano (varias RK de escopo minimo) chega ao teto e recebe um
// `409 active_credential_limit_reached` sem caminho de saida obvio.
//
// A mitigacao escolhida e a MAIS BARATA das tres (§7.2): a tela empurra a
// faxina usando o `last_used_at` que a V2 JA devolve no GET /v1/tokens
// (portal_tokens.py, coluna `oac.last_used_at`, escrita a cada auth bem
// sucedida). Zero backend novo, zero mudanca de contrato.
//
// O que este modulo NAO faz, deliberadamente: nao revoga nada sozinho.
// Revogacao automatica por inatividade e a opcao 3 do §7.2, NAO recomendada —
// derrubaria integracao viva que so roda trimestralmente, e e enforcement, que
// a decisao D5 do owner veta. Aqui so ha leitura, ranking e sugestao.

// A partir de quantos dias sem uso a credencial e sinalizada como candidata.
// Nao e enforcement nem prazo de expiracao: e o corte do DESTAQUE visual.
export const STALE_AFTER_DAYS = 30;

// Vagas restantes a partir das quais a tela oferece a faxina (§7.2: "<= 2").
export const CLEANUP_PROMPT_THRESHOLD = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

// Dias inteiros decorridos desde um instante ISO. `null` para ausente/invalido
// — NUNCA 0, que se confundiria com "usada hoje".
export function daysSince(iso, now = Date.now()) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return null;
  const elapsed = now - then;
  if (elapsed < 0) return 0; // relogio adiantado do cliente nao vira negativo
  return Math.floor(elapsed / DAY_MS);
}

// Leitura do uso de UMA credencial. `neverUsed` distingue o caso mais gritante
// (nunca autenticou desde a criacao) de "usada ha muito tempo".
export function lastUsedSummary(token, now = Date.now()) {
  const days = daysSince(token?.last_used_at, now);
  if (days === null) {
    const ageDays = daysSince(token?.created_at, now);
    return {
      neverUsed: true,
      days: null,
      ageDays,
      stale: ageDays === null ? true : ageDays >= STALE_AFTER_DAYS,
      label: "nunca usada",
    };
  }
  return {
    neverUsed: false,
    days,
    ageDays: daysSince(token?.created_at, now),
    stale: days >= STALE_AFTER_DAYS,
    label:
      days === 0 ? "usada hoje" : days === 1 ? "não usada há 1 dia" : `não usada há ${days} dias`,
  };
}

// Vagas livres. `null` quando a V2 nao ecoou `active_limit` (rollout de versao
// antiga) — a tela nao pode INVENTAR teto, mesmo padrao de tokenCapacityReached.
export function slotsRemaining(activeCount, activeLimit) {
  if (!Number.isInteger(activeLimit) || activeLimit < 0) return null;
  return Math.max(0, activeLimit - (Number.isInteger(activeCount) ? activeCount : 0));
}

export function shouldPromptCleanup(activeCount, activeLimit) {
  const left = slotsRemaining(activeCount, activeLimit);
  return left !== null && left <= CLEANUP_PROMPT_THRESHOLD;
}

// UMA moldura para a metrica de teto (gate UX, L2).
//
// A tela tinha DOIS enquadramentos da MESMA metrica: o badge do topo contava
// ocupacao ("8 de 10 credenciais ativas") e o painel de faxina contava o
// complemento ("Restam 2 de 10 vagas"). Dois numeros para o mesmo fato obrigam
// o master a fazer a subtracao de cabeca para saber se sao a mesma coisa.
//
// Aqui a OCUPACAO e o numero primario (mesma frase do badge) e a pressao de
// vaga vira QUALIFICADOR derivado dela. Badge e painel consomem esta funcao —
// e estruturalmente impossivel os dois divergirem de novo.
//
// `null` quando nao ha teto conhecido (V2 sem `active_limit`): sem teto nao ha
// ocupacao a enquadrar, mesmo fail-closed de slotsRemaining.
export function capacityFraming(activeCount, activeLimit) {
  const left = slotsRemaining(activeCount, activeLimit);
  if (left === null) return null;
  const count = Number.isInteger(activeCount) ? activeCount : 0;
  return {
    slotsLeft: left,
    primary: `${count} de ${activeLimit} credenciais ativas`,
    qualifier:
      left === 0 ? "sem vagas" : left === 1 ? "última vaga" : `restam ${left} vagas`,
  };
}

// Uma credencial so e CANDIDATA quando ha sinal de ociosidade: sem uso ha
// STALE_AFTER_DAYS ou nunca usada. Credencial EM USO nunca entra na lista.
//
// PORQUE isto e FILTRO e nao so ordenacao: o painel oferece, em UM CLIQUE, uma
// acao IRREVERSIVEL que derruba integracao em producao. A versao anterior
// apenas ranqueava — a credencial usada ontem ia para o fim da fila, mas com o
// botao "Revogar" do lado. Pressao de vaga nao pode transformar "revise" em
// "revogue a de baixo".
export function isIdleCandidate(usage) {
  return usage?.stale === true || usage?.neverUsed === true;
}

// Ranking das candidatas — SO credenciais ativas E ociosas; nunca revogadas nem
// expiradas (essas ja nao consomem vaga, revoga-las nao libera nada).
// Ordem: nunca usadas primeiro (as mais antigas na frente), depois as de uso
// mais remoto. Empate resolvido por `created_at` para o ranking ser estavel.
export function revocationCandidates(tokens, now = Date.now()) {
  const active = (Array.isArray(tokens) ? tokens : []).filter(
    (token) => token?.status === "active"
  );
  return active
    .map((token) => ({ token, usage: lastUsedSummary(token, now) }))
    .filter(({ usage }) => isIdleCandidate(usage))
    .sort((a, b) => {
      if (a.usage.neverUsed !== b.usage.neverUsed) return a.usage.neverUsed ? -1 : 1;
      if (!a.usage.neverUsed && a.usage.days !== b.usage.days) return b.usage.days - a.usage.days;
      return (b.usage.ageDays ?? 0) - (a.usage.ageDays ?? 0);
    });
}
